import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useOtpAnalytics } from "../hooks/use-otp-logs";
import { format, parseISO } from "date-fns";

interface OtpTimeSeriesChartProps {
  startDate?: string;
  endDate?: string;
}

export function OtpTimeSeriesChart({
  startDate,
  endDate,
}: OtpTimeSeriesChartProps) {
  const { data: analytics, isLoading, error } = useOtpAnalytics({ startDate, endDate });

  if (isLoading || !analytics) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>OTP Volume Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80 animate-pulse bg-gray-100 rounded"></div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>OTP Volume Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80 flex items-center justify-center text-muted-foreground">
            Failed to load chart data
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if timeSeries exists and has data
  if (!analytics.timeSeries || analytics.timeSeries.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>OTP Volume Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80 flex items-center justify-center text-muted-foreground">
            No data available for the selected time period
          </div>
        </CardContent>
      </Card>
    );
  }

  // Transform data with error handling for date parsing
  const chartData = analytics.timeSeries.map((item) => {
    try {
      // Handle different date formats from backend
      let dateStr: string;
      const dateValue = item.date as unknown;
      
      // If date is already a Date object (shouldn't happen but handle it)
      if (dateValue instanceof Date) {
        dateStr = dateValue.toISOString().split('T')[0];
      } else if (typeof dateValue === 'string') {
        dateStr = dateValue;
      } else {
        throw new Error('Date is not a string or Date object');
      }
      
      // Remove time portion if present
      dateStr = dateStr.split('T')[0];
      // Add timezone for parsing
      const dateWithTime = `${dateStr}T00:00:00Z`;
      const parsedDate = parseISO(dateWithTime);
      
      // Validate the parsed date
      if (isNaN(parsedDate.getTime())) {
        throw new Error('Invalid date');
      }
      
      return {
        ...item,
        date: format(parsedDate, "MMM dd"),
        // Ensure all numeric values are numbers
        total: Number(item.total) || 0,
        sent: Number(item.sent) || 0,
        delivered: Number(item.delivered) || 0,
        verified: Number(item.verified) || 0,
        failed: Number(item.failed) || 0,
      };
    } catch (error) {
      // Fallback: use the date string as-is if parsing fails
      console.warn('Failed to parse date:', item.date, error);
      return {
        ...item,
        date: String(item.date),
        // Ensure all numeric values are numbers
        total: Number(item.total) || 0,
        sent: Number(item.sent) || 0,
        delivered: Number(item.delivered) || 0,
        verified: Number(item.verified) || 0,
        failed: Number(item.failed) || 0,
      };
    }
  }).filter((item) => item.date); // Filter out any invalid entries

  // Final validation - ensure we have valid chart data
  if (!chartData || chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>OTP Volume Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80 flex items-center justify-center text-muted-foreground">
            No valid data available for the chart
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>OTP Volume Over Time</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="total"
              stroke="#3b82f6"
              strokeWidth={2}
              name="Total"
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="sent"
              stroke="#10b981"
              strokeWidth={2}
              name="Sent"
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="delivered"
              stroke="#06b6d4"
              strokeWidth={2}
              name="Delivered"
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="verified"
              stroke="#8b5cf6"
              strokeWidth={2}
              name="Verified"
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="failed"
              stroke="#ef4444"
              strokeWidth={2}
              name="Failed"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

